import type { Order, StoreInfo } from '@multicrm/shared';
import { formatMoney, orderStatusLabel, relativeTime } from '@multicrm/shared';
import { motion } from 'motion/react';
import { PhoneOff, PackageX } from 'lucide-react';
import { memo } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '../lib/cn';
import { useI18n } from '../lib/i18n';
import { haptics } from '../lib/telegram';
import { useDemo, maskName, maskStoreName } from '../lib/demo';
import { StatusBadge, TrackingBadge } from './ui/Badge';

/**
 * Карточка заказа. На ней ровно то, по чему менеджер принимает решение:
 * номер, статус, клиент, куда едет и сколько денег. Остальное — в карточке.
 */
export const OrderCard = memo(function OrderCard({
  order,
  store,
  highlight,
}: {
  order: Order;
  store: StoreInfo | undefined;
  /** заказ только что изменился — подсвечиваем, чтобы было видно, что нового */
  highlight?: boolean;
}) {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { isDemo } = useDemo();

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 8 }}
      animate={{
        opacity: 1,
        y: 0,
        // Подсветка живёт секунду и гаснет сама: моргнуло — и дальше.
        backgroundColor: highlight ? 'var(--tone-green-bg)' : 'var(--surface-3)',
      }}
      transition={{ duration: 0.22, backgroundColor: { duration: highlight ? 0.25 : 1.1 } }}
      onClick={() => {
        haptics.tap();
        void navigate(`/orders/${encodeURIComponent(order.ref)}`);
      }}
      className="border-line rounded-card w-full border p-3.5 text-left active:scale-[0.99] gpu"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {store &&
            (store.icon && !isDemo ? (
              <img
                src={store.icon}
                alt=""
                className="h-4 w-4 shrink-0 rounded-full"
                title={maskStoreName(store.label, isDemo)}
              />
            ) : (
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: store.color }}
                title={maskStoreName(store.label, isDemo)}
              />
            ))}
          <span className="truncate text-[15px] font-semibold">№{order.number}</span>
          <span className="text-muted shrink-0 text-[12px]">
            {relativeTime(order.createdAt, new Date(), locale === 'ru' ? 'ru-RU' : 'uk-UA')}
          </span>
        </span>

        <StatusBadge
          status={order.status}
          label={orderStatusLabel(order.status, locale, store?.statusLabels)}
        />
      </div>

      <p className="mt-2 truncate text-[15px]">{maskName(order.customer.name, isDemo)}</p>

      {(order.delivery.cityName || order.delivery.branchName) && (
        <p className="text-muted mt-0.5 truncate text-[13px]">
          {[order.delivery.cityName, order.delivery.branchName].filter(Boolean).join(' · ')}
        </p>
      )}

      {order.delivery.trackingStatus && (
        <div className="mt-2.5 flex items-center gap-1.5 truncate text-[13px]">
          <TrackingBadge status={order.delivery.trackingStatus} />
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="text-[16px] font-semibold tracking-tight">
            {formatMoney(order.amount, order.currency, locale === 'ru' ? 'ru-RU' : 'uk-UA')}
          </span>
          {order.itemsCount > 0 && (
            <span className="text-muted text-[12px]">
              {order.itemsCount} {t('orders.itemsCount')}
            </span>
          )}
        </span>

        <span className="text-muted flex items-center gap-2">
          {order.flags.noCall && <PhoneOff size={14} className={cn('text-[var(--tone-amber)]')} />}
          {order.flags.nonPickup && <PackageX size={14} className="text-danger" />}
        </span>
      </div>
    </motion.button>
  );
});
